import { IPQuestion } from "modules/organization/models/interface";
import { EquationParser, ILogicalExpression } from "./equationParser";

const OPERATOR_PATTERN =
  /^(AND|QAND|OR|QOR|IMPORT|EQUAL|NOTEQUAL|COMPARE|QHASANSWER|QHASNOTANSWER)$/;

const COMPARE_OPERATORS = ["=", "!=", "<", ">", ">=", "<="];

const COMPARE_TYPES = ["NUMBER", "DATE"];

// Walks an already-parsed (syntactically valid) equation tree and collects EVERY content error
// found — unknown variable, wrong argument count for an operator, unknown IMPORT target,
// IMPORT tier-order violation — rather than stopping at the first one, so all of them can be
// surfaced together. `exprPair` is always `[operatorName, argsArray]`; `argsArray` is a mix of
// plain string arguments belonging to this operator and nested `[operatorName, argsArray]` pairs
// (sub-expressions), which are recursed into independently.
const collectEquationContentErrors = (
  exprPair: ILogicalExpression,
  knownQuestionVars: Set<string>,
  knownResponseVars: Set<string>,
  rankDisplayOrderByName: Map<string, number | null | undefined>,
  selfDisplayOrder: number | null | undefined,
  errors: Set<string>,
) => {
  const operatorName =
    typeof exprPair[0] === "string" ? exprPair[0].toUpperCase() : "";
  const argsArray = exprPair[1];
  if (!Array.isArray(argsArray)) {
    return;
  }

  // Only AND/OR's calculation engine (calculateEquation.ts's operatorAND/operatorOR) actually
  // accepts a nested operator call as one of its own arguments (e.g. AND(a, OR(b,c))) — every
  // other operator (IMPORT, EQUAL, COMPARE, QAND, QHASANSWER, etc.) only ever takes plain string
  // arguments. So the "this looks like a nested sub-expression, validate it separately and don't
  // count it" treatment below must only apply for AND/OR — otherwise e.g.
  // IMPORT(Rank2, AND(x)) silently swallows "AND", [...] as an unrelated nested pair, leaving
  // plainArgs at length 1 ("Rank2") and hiding the real problem: IMPORT was actually given two
  // arguments, and it doesn't support a nested expression as either of them.
  const allowsNestedExpressions = operatorName === "AND" || operatorName === "OR";

  // Only plain string arguments count toward an operator's own parameter list/role checks — a
  // nested operator call (e.g. AND's argument being another OR(...)) is validated independently
  // via the recursive call below, not counted as one of THIS operator's plain parameters.
  const plainArgs: string[] = [];
  for (let i = 0; i < argsArray.length; i++) {
    const item = argsArray[i];
    const next = argsArray[i + 1];
    if (
      allowsNestedExpressions &&
      typeof item === "string" &&
      OPERATOR_PATTERN.test(item.toUpperCase()) &&
      Array.isArray(next)
    ) {
      collectEquationContentErrors(
        [item, next],
        knownQuestionVars,
        knownResponseVars,
        rankDisplayOrderByName,
        selfDisplayOrder,
        errors,
      );
      i++; // the nested pair consumed `next` too — don't re-visit it as a plain arg
      continue;
    }
    if (typeof item === "string") {
      plainArgs.push(item);
    }
  }

  if (operatorName === "AND" || operatorName === "OR") {
    // The real calculation engine (calculateEquation.ts) places no minimum/maximum on AND/OR's
    // argument count — 0 args silently computes to 0 rather than throwing — so no count check is
    // added here either, to avoid blocking Save on something that would actually run fine.
    plainArgs.forEach((variable) => {
      if (!knownResponseVars.has(variable)) {
        errors.add(`Response variable not found: ${variable}`);
      }
    });
  } else if (
    operatorName === "QAND" ||
    operatorName === "QOR" ||
    operatorName === "QHASANSWER" ||
    operatorName === "QHASNOTANSWER"
  ) {
    if (plainArgs.length !== 1) {
      errors.add(
        `${operatorName} takes exactly one question variable (found ${plainArgs.length}).`,
      );
    } else if (!knownQuestionVars.has(plainArgs[0])) {
      errors.add(`Question variable not found: ${plainArgs[0]}`);
    }
  } else if (operatorName === "EQUAL" || operatorName === "NOTEQUAL") {
    if (plainArgs.length !== 2) {
      errors.add(
        `${operatorName} takes exactly two parameters — a response variable and a comparison value (found ${plainArgs.length}).`,
      );
    } else if (!knownResponseVars.has(plainArgs[0])) {
      errors.add(`Response variable not found: ${plainArgs[0]}`);
    }
  } else if (operatorName === "COMPARE") {
    if (plainArgs.length !== 3 && plainArgs.length !== 4) {
      errors.add(
        `COMPARE takes three parameters — a response variable, a comparison operator, and a value — plus an optional fourth parameter (NUMBER or DATE) (found ${plainArgs.length}).`,
      );
    } else {
      if (!knownResponseVars.has(plainArgs[0])) {
        errors.add(`Response variable not found: ${plainArgs[0]}`);
      }
      if (!COMPARE_OPERATORS.includes(plainArgs[1])) {
        errors.add(
          `COMPARE's second parameter must be one of ${COMPARE_OPERATORS.join(", ")} — found '${plainArgs[1]}'.`,
        );
      }
      if (
        plainArgs.length === 4 &&
        !COMPARE_TYPES.includes(plainArgs[3].toUpperCase())
      ) {
        errors.add(
          `COMPARE's fourth parameter must be one of ${COMPARE_TYPES.join(", ")} — found '${plainArgs[3]}'.`,
        );
      }
    }
  } else if (operatorName === "IMPORT") {
    if (plainArgs.length !== 1) {
      errors.add(
        `IMPORT takes exactly one rank/equation name (found ${plainArgs.length}).`,
      );
    } else {
      const rankName = plainArgs[0];
      if (!rankDisplayOrderByName.has(rankName)) {
        errors.add(`Rank not found: ${rankName}`);
      } else if (selfDisplayOrder != null) {
        const targetDisplayOrder = rankDisplayOrderByName.get(rankName);
        if (
          targetDisplayOrder != null &&
          targetDisplayOrder < selfDisplayOrder
        ) {
          errors.add(
            `${rankName} is a higher rank and cannot be imported into a lower rank.`,
          );
        }
      }
    }
  }
};

// Single source of truth for "is this equation OK to save" — syntax first (a specific, pointed
// reason, not a generic label), then every content problem found (unknown variable, wrong
// argument count per operator, unknown/higher-rank IMPORT target). Used both for the Equation
// Editor's live, per-keystroke error list and as a server-call guard in the store's
// saveRankingEquation, so the same rules apply whichever way a save is triggered.
export const validateEquationContent = (
  equationText: string,
  questions: IPQuestion[],
  rankDisplayOrderByName: Map<string, number | null | undefined>,
  selfDisplayOrder: number | null | undefined,
): string[] => {
  if (!equationText.trim()) {
    return [];
  }

  const equationParsedObj = new EquationParser(equationText);
  if (equationParsedObj.getHasError()) {
    return [equationParsedObj.getSyntaxErrorMessage()];
  }

  const knownQuestionVars = new Set(
    questions.map((each) => each.question_variable),
  );
  const knownResponseVars = new Set(
    questions.flatMap((each) =>
      each.responses.map((eachRes) => eachRes.response_variable),
    ),
  );

  const errors = new Set<string>();
  const exprPair = equationParsedObj.getEquationStack();
  if (Array.isArray(exprPair) && exprPair.length === 2) {
    collectEquationContentErrors(
      exprPair,
      knownQuestionVars,
      knownResponseVars,
      rankDisplayOrderByName,
      selfDisplayOrder,
      errors,
    );
  }
  return Array.from(errors);
};
